import React from 'react';
import { Book } from '../../types/book';

interface BookCardProps {
  book: Book;
  onDelete: (id: string) => void;
  onEdit: (id: string) => void;
  onClick: () => void;
}

export const BookCard: React.FC<BookCardProps> = ({ book, onDelete, onEdit, onClick }) => {
  // Fallback image for broken/missing thumbnails
  const fallback = '/default-book-cover.png'; // Place a default image in your public folder
  const [imgSrc, setImgSrc] = React.useState(book.coverUrl || fallback);

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (window.confirm('Are you sure you want to delete this book? This action cannot be undone.')) {
      onDelete(book.id);
    }
  };

  return (
    <div
      className="group relative bg-white rounded-lg overflow-hidden transition-all duration-200 ease-in-out cursor-pointer transform hover:-translate-y-1 hover:shadow-lg"
      onClick={onClick}
    >
      {/* Cover Image Container with fixed aspect ratio */}
      <div className="relative pb-[150%]">
        <img
          src={imgSrc}
          alt={book.title}
          className="absolute inset-0 w-full h-full object-cover"
          onError={() => setImgSrc(fallback)}
        />
      </div>

      {/* Content */}
      <div className="p-3">
        <h3 className="text-sm font-medium text-gray-900 leading-snug line-clamp-2 mb-1">
          {book.title}
        </h3>
        <p className="text-xs text-gray-600 mb-2">by {book.author}</p>

        {/* Genre Tag */}
        <div>
          <span className="inline-block text-[11px] px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full">
            {book.genre}
          </span>
        </div>
      </div>
      
      {/* Hover Actions */}
      <div className="absolute top-2 right-2 flex space-x-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          onClick={(e) => {
            e.stopPropagation();
            onEdit(book.id);
          }}
          className="p-1.5 bg-white/90 backdrop-blur-sm rounded-full shadow-sm hover:bg-white transition-colors"
        >
          <svg className="w-3.5 h-3.5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
        </button>
        <button
          onClick={handleDelete}
          className="p-1.5 bg-white/90 backdrop-blur-sm rounded-full shadow-sm hover:bg-white transition-colors"
        >
          <svg className="w-3.5 h-3.5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        </button>
      </div>
    </div>
  );
}; 